import { jsPDF } from 'jspdf';
import { STAGE_CONFIGS, STAGE_ORDER } from '../../data/stageConfigs';

// Built-in Helvetica can't render emoji or most unicode symbols; strip them for
// PDF output (on-screen display keeps them).
function stripUnsupportedGlyphs(s: string): string {
  return s
    .replace(
      /[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{1F000}-\u{1F9FF}\u{1F100}-\u{1F1FF}]/gu,
      '',
    )
    .replace(/[←-⇿]/g, '->')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export interface PlanSection {
  title: string;
  body: string;
}

/** Parse `## Heading\nbody...` blocks out of the LLM output. */
export function parsePlanSections(text: string): PlanSection[] {
  const sections: PlanSection[] = [];
  const lines = text.split('\n');
  let current: PlanSection | null = null;
  for (const raw of lines) {
    const line = raw.replace(/\r$/, '');
    const m = /^#{1,3}\s+(.*)$/.exec(line.trim());
    if (m) {
      if (current) sections.push(current);
      current = { title: m[1].trim(), body: '' };
      continue;
    }
    if (!current) {
      // Text before the first heading — treat as intro.
      if (line.trim()) {
        current = { title: 'Intro', body: line };
      }
      continue;
    }
    current.body += (current.body ? '\n' : '') + line;
  }
  if (current) sections.push(current);
  return sections
    .map((s) => ({ title: s.title, body: s.body.trim() }))
    .filter((s) => s.body);
}

interface GenerateArgs {
  planText: string;
  answers: Record<string, Record<string, string>>;
  explorerName: string;
}

const MARGIN = 48;
const AMBER: [number, number, number] = [217, 119, 6];
const MINT: [number, number, number] = [6, 148, 107];
const VIOLET: [number, number, number] = [124, 58, 237];
const BODY: [number, number, number] = [30, 30, 36];
const MUTED: [number, number, number] = [110, 110, 120];

export function generatePlanPdf({ planText, answers, explorerName }: GenerateArgs): jsPDF {
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;
  let y = MARGIN;

  const setColor = (c: [number, number, number]) => doc.setTextColor(c[0], c[1], c[2]);

  const writeText = (
    text: string,
    opts: {
      size?: number;
      bold?: boolean;
      italic?: boolean;
      color?: [number, number, number];
      gapAfter?: number;
      lineHeight?: number;
    } = {},
  ) => {
    if (!text) return;
    const size = opts.size ?? 11;
    const weight = opts.bold ? 'bold' : opts.italic ? 'italic' : 'normal';
    doc.setFontSize(size);
    doc.setFont('helvetica', weight);
    setColor(opts.color ?? BODY);
    const lh = opts.lineHeight ?? size * 1.38;
    const cleaned = stripUnsupportedGlyphs(text);
    const lines = doc.splitTextToSize(cleaned, contentWidth) as string[];
    for (const line of lines) {
      if (y + lh > pageHeight - MARGIN) {
        doc.addPage();
        y = MARGIN;
      }
      doc.text(line, MARGIN, y);
      y += lh;
    }
    if (opts.gapAfter) y += opts.gapAfter;
  };

  const addDivider = (color: [number, number, number] = AMBER, gap = 14) => {
    if (y + 6 > pageHeight - MARGIN) {
      doc.addPage();
      y = MARGIN;
    }
    doc.setDrawColor(color[0], color[1], color[2]);
    doc.setLineWidth(1);
    doc.line(MARGIN, y, MARGIN + 36, y);
    y += gap;
  };

  // ---------- Cover page ----------
  doc.setFillColor(13, 15, 28);
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  // accent bar
  doc.setFillColor(AMBER[0], AMBER[1], AMBER[2]);
  doc.rect(0, 0, pageWidth, 6, 'F');
  doc.setFillColor(MINT[0], MINT[1], MINT[2]);
  doc.rect(0, pageHeight - 6, pageWidth, 6, 'F');

  // Title
  setColor([255, 255, 255]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(42);
  doc.text('Business Plan', MARGIN, pageHeight / 2 - 40);

  setColor([230, 195, 100]);
  doc.setFontSize(22);
  doc.setFont('helvetica', 'normal');
  doc.text(
    `by ${stripUnsupportedGlyphs(explorerName) || 'Explorer'}`,
    MARGIN,
    pageHeight / 2 - 8,
  );

  setColor([180, 180, 190]);
  doc.setFontSize(12);
  const today = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  doc.text(today, MARGIN, pageHeight / 2 + 22);

  setColor([120, 120, 130]);
  doc.setFontSize(10);
  doc.text('Generated by KoKo  -  biz.omore.app', MARGIN, pageHeight - MARGIN);

  // ---------- Plan content ----------
  doc.addPage();
  y = MARGIN;

  writeText("KoKo's Write-Up", {
    size: 22,
    bold: true,
    color: AMBER,
    gapAfter: 4,
  });
  addDivider(AMBER, 12);

  const sections = parsePlanSections(planText);
  if (sections.length === 0) {
    writeText(planText, { size: 11, gapAfter: 6 });
  } else {
    for (const section of sections) {
      writeText(section.title, {
        size: 14,
        bold: true,
        color: MINT,
        gapAfter: 4,
      });
      writeText(section.body, { size: 11, gapAfter: 14 });
    }
  }

  // ---------- Answers section ----------
  doc.addPage();
  y = MARGIN;

  writeText('Your Answers', {
    size: 22,
    bold: true,
    color: AMBER,
    gapAfter: 4,
  });
  addDivider(AMBER, 14);

  for (const key of STAGE_ORDER) {
    const cfg = STAGE_CONFIGS[key];
    if (!cfg) continue;
    const stageAnswers = answers[key] ?? {};
    const filled = cfg.questions
      .map((q) => ({ prompt: q.prompt, value: (stageAnswers[q.id] ?? '').toString().trim() }))
      .filter((q) => q.value.length > 0);

    writeText(`Stage ${cfg.number}  -  ${cfg.name}`, {
      size: 14,
      bold: true,
      color: VIOLET,
      gapAfter: 6,
    });

    if (filled.length === 0) {
      writeText('(no answers captured)', {
        size: 10,
        italic: true,
        color: MUTED,
        gapAfter: 14,
      });
      continue;
    }

    for (const f of filled) {
      writeText(f.prompt, { size: 9, bold: true, color: MUTED, gapAfter: 2 });
      writeText(f.value, { size: 11, gapAfter: 10 });
    }
    y += 6;
  }

  // ---------- Footer on every page ----------
  const pageCount = doc.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    setColor(MUTED);
    if (i > 1) {
      doc.text(
        `${stripUnsupportedGlyphs(explorerName) || 'Explorer'}  -  Business Plan  -  ${i} / ${pageCount}`,
        MARGIN,
        pageHeight - 24,
      );
    }
  }

  return doc;
}

export function downloadPlanPdf(args: GenerateArgs) {
  const doc = generatePlanPdf(args);
  const safeName = (args.explorerName || 'explorer').replace(/[^a-z0-9-_]+/gi, '_');
  doc.save(`${safeName}-business-plan.pdf`);
}
